/**
 * Discovery / Classifier - Phase 2 part 2 wiring.
 * Source: GET /v1/discovery/classifiers
 */
import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { Cpu, Brain, GitBranch, Box, Atom, CheckCircle2, Loader2, AlertTriangle, FlaskConical, Power, Plus, ShieldCheck } from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useUiStore } from '@/stores/ui';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import {
  listClassifiers,
  classifySamples,
  setDetectorEnabled,
  createClassifierVocab,
  type ClassifierCatalogue,
  type ClassifySamplesResult,
  type ClassifierChecksum,
} from '@/lib/api';

const CHECKSUM_OPTIONS: Array<{ value: ClassifierChecksum; label: string }> = [
  { value: 'none', label: 'No checksum (pattern only)' },
  { value: 'luhn', label: 'Luhn (cards, IMEI)' },
  { value: 'verhoeff', label: 'Verhoeff (Aadhaar-style)' },
  { value: 'gstin_mod36', label: 'GSTIN mod-36' },
  { value: 'mod11', label: 'Mod-11 (account/ID)' },
];
// Default pack the on-page custom-rule builder writes to. Rule EXECUTION is
// pack-independent (the classify path runs every enabled regex rule), so this
// only tags provenance. The pack-aware editor lives at /admin/classifier-vocab.
const FALLBACK_RULE_PACK = 'bfsi_banking';

const TIER_ICONS = { 1: Cpu, 2: Brain, 3: GitBranch, 4: Box, 5: Atom } as const;
const TIER_DESCRIPTIONS: Record<number, { name: string; subtitle: string }> = {
  1: { name: 'Tier 1 - Regex + Checksum', subtitle: 'Deterministic detectors with format validation (Verhoeff for Aadhaar, Luhn for PAN, MOD-10).' },
  2: { name: 'Tier 2 - DistilBERT-Indic NER', subtitle: 'Multilingual named-entity recognition for contextual PII that Tier 1 patterns miss.' },
  3: { name: 'Tier 3 - Column-Graph Sibling-Walk', subtitle: 'Propagates classifier confidence across sibling columns within an asset (when ≥50% of siblings carry PII, the column gets a PII-context boost).' },
  4: { name: 'Tier 4 - pgvector Embeddings', subtitle: 'Catches obfuscated / encoded PII variants via vector similarity.' },
  5: { name: 'Tier 5 - BYO-LLM Tie-breaker', subtitle: 'Optional opt-in LLM verdict for ambiguous cases (Sovereign-tier).' },
};

// Plain-language "what this catches" for each detector, keyed by label. Shown
// so a non-technical buyer understands exactly what is scanned. Any detector
// not listed falls back to a generic caption.
const DETECTOR_DESCRIPTIONS: Record<string, string> = {
  aadhaar: "India's 12-digit national ID (UIDAI). Verified with the Verhoeff checksum.",
  pan: 'Permanent Account Number - the 10-character income-tax ID.',
  gstin: '15-character GST registration number, with its own check digit.',
  ifsc: '11-character bank-branch code used for transfers (RBI).',
  bank_account: 'Bank account numbers.',
  voter_id: 'EPIC voter-ID card number (Election Commission).',
  driving_licence: 'Indian driving-licence numbers.',
  passport: 'Passport numbers.',
  credit_card: 'Payment-card numbers (Visa/Mastercard/RuPay/Amex). Verified with the Luhn checksum.',
  phone: 'Indian mobile numbers (10-digit / +91).',
  email: 'Email addresses.',
  date_of_birth: 'Dates of birth.',
  pincode: '6-digit Indian postal (PIN) codes.',
  aws_access_key: 'AWS access-key IDs accidentally left in code, configs or CSV dumps.',
  db_connection_string: 'Database connection strings that carry an inline password.',
  api_key: 'Secret-named, high-entropy API keys.',
  private_key: 'PEM private-key blocks (SSH / TLS / signing keys).',
  jwt_token: 'JWT bearer tokens (eyJ... session/auth tokens).',
};

export function DiscoveryClassifier() {
  const { selectedBusinessUnitId, selectedBusinessUnitName } = useUiStore();
  const [data, setData] = useState<ClassifierCatalogue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Wave 5 Group-A (2026-05-27) - on-demand classify panel. POSTs a
  // column name + sample values to /v1/discovery/classify and shows
  // the multi-engine voter's verdict. Useful for DPO/Compliance to
  // sanity-check a column's classification before approving an audit.
  const [classifyCol, setClassifyCol] = useState('cust_aadhaar_id');
  const [classifySamples_, setClassifySamples_] = useState(
    '123456789012\n234567890123\n345678901234',
  );
  // OCR provenance. Off by default so the panel mirrors the scanner's digital-
  // text behaviour; turning it on tells the API these samples came from a
  // scanned document, which enables the OCR-confusion repair.
  const [classifyFromOcr, setClassifyFromOcr] = useState(false);
  const [classifying, setClassifying] = useState(false);
  const [classifyResult, setClassifyResult] = useState<ClassifySamplesResult | null>(null);
  const [classifyError, setClassifyError] = useState<string | null>(null);

  // Modular Rule Engine - per-detector enable/disable + custom-rule builder.
  const [detectorBusy, setDetectorBusy] = useState<string | null>(null);
  const [ruleLabel, setRuleLabel] = useState('employee_id');
  const [rulePattern, setRulePattern] = useState('EMP-\\d{6}');
  const [ruleChecksum, setRuleChecksum] = useState<ClassifierChecksum>('none');
  const [ruleBusy, setRuleBusy] = useState(false);
  const [ruleMsg, setRuleMsg] = useState<string | null>(null);

  async function toggleDetector(label: string, next: boolean): Promise<void> {
    setDetectorBusy(label);
    // Optimistic update so the switch feels instant.
    // Optimistically flip BOTH lists: a label can appear as a Tier-1 built-in
    // and as pack vocabulary, and one switch governs both - the classify path
    // drops the label wherever it came from.
    const flip = (prev: ClassifierCatalogue | null, on: boolean) =>
      prev
        ? {
            ...prev,
            detectors: prev.detectors.map((d) => (d.label === label ? { ...d, enabled: on } : d)),
            ...(prev.industryPacks
              ? {
                  industryPacks: {
                    ...prev.industryPacks,
                    detectors: prev.industryPacks.detectors.map((d) =>
                      d.label === label ? { ...d, enabled: on } : d,
                    ),
                  },
                }
              : {}),
          }
        : prev;
    setData((prev) => flip(prev, next));
    try {
      await setDetectorEnabled(label, next, selectedBusinessUnitId);
    } catch {
      // revert on failure
      setData((prev) => flip(prev, !next));
    } finally {
      setDetectorBusy(null);
    }
  }

  async function addCustomRule(): Promise<void> {
    if (!ruleLabel.trim() || !rulePattern.trim()) return;
    setRuleBusy(true);
    setRuleMsg(null);
    try {
      await createClassifierVocab(
            // Tag the rule with a pack actually in force for this unit. It was
            // hardcoded to bfsi_banking, so a rule authored on a hospital unit
            // was filed under banking.
            ((data?.industryPacks?.packIds[0] ?? FALLBACK_RULE_PACK) as never),
            {
        patternKind: 'regex',
        pattern: rulePattern.trim(),
        label: ruleLabel.trim(),
        confidenceBoost: 0.9,
        checksum: ruleChecksum,
      });
      setRuleMsg(`Added custom rule "${ruleLabel.trim()}"${ruleChecksum !== 'none' ? ` with ${ruleChecksum} checksum` : ''}. Test it below.`);
      await refresh();
    } catch (e) {
      setRuleMsg(e instanceof Error ? e.message : 'Failed to add rule');
    } finally {
      setRuleBusy(false);
    }
  }

  async function handleClassify(): Promise<void> {
    setClassifying(true);
    setClassifyResult(null);
    setClassifyError(null);
    try {
      const samples = classifySamples_
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
      const r = await classifySamples({ columnName: classifyCol, sampleValues: samples, fromOcr: classifyFromOcr });
      setClassifyResult(r);
    } catch (e) {
      setClassifyError(e instanceof Error ? e.message : 'classify failed');
    } finally {
      setClassifying(false);
    }
  }

  const refresh = (signal?: AbortSignal) =>
    listClassifiers(selectedBusinessUnitId)
      .then((res) => !signal?.aborted && setData(res))
      .catch((err) => !signal?.aborted && setError(err instanceof Error ? err.message : 'load failed'))
      .finally(() => !signal?.aborted && setLoading(false));

  // Refetch when the header's business unit changes. Tier 1 is the same
  // everywhere, but the industry packs are not - and until this dependency
  // existed, selecting a hospital unit re-rendered the identical banking-and-
  // general detector list, which is exactly why packs looked inert.
  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    refresh(ctrl.signal);
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBusinessUnitId]);

  const tierAvailability: Record<number, boolean> = {
    1: true,
    2: data?.tier2Available ?? false,
    3: data?.tier3Available ?? false,
    4: data?.tier4Available ?? false,
    5: data?.tier5Available ?? false,
  };

  return (
    <PageContainer>
      <PageHeader
        title="Classification engine"
        subtitle="Five-tier classification with voter aggregation. Tier 1 ships today; Tiers 2-5 land in subsequent phases."
        badges={[
          { label: '5 tiers', variant: 'glass' },
          ...(loading
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : error
              ? [{ label: 'Degraded', variant: 'warning' as const }]
              : [{ label: 'Live', variant: 'success' as const }]),
        ]}
      />

      {/* Plain-language explainer - buyers are not always technical. Explain the
          false-positive-killer (checksum) idea and what "your control" means before
          the tier grid. */}
      <div className="glass-lite rounded-md p-4 mb-5 border border-primary/20">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck className="h-4 w-4 text-primary shrink-0" />
          <span className="text-sm font-semibold text-foreground/90">How this avoids false positives</span>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">
          A random 12-digit number is <em>not</em> an Aadhaar number. Every detector runs a maths check
          (a <strong className="text-foreground/80">checksum</strong>) on top of the pattern - Aadhaar
          uses Verhoeff, credit cards use Luhn, GSTIN its own check digit - so a value is only flagged
          when the maths confirms it. That is how you get <strong className="text-foreground/80">exact
          counts</strong>, not a wall of false alarms.
        </p>
        <div className="mt-2.5 flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-emerald-400" /> Tier 1 below is live today - format + checksum</span>
          <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-emerald-400" /> Click any detector to switch it off</span>
          <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-emerald-400" /> Add your own rules with their own checksum</span>
        </div>
      </div>

      {loading && <LoadingState caption="Loading classifier catalogue…" />}
      {error && !loading && (
        <ErrorState
          title="Failed to load classifiers"
          detail={error}
          onRetry={() => { setError(null); setLoading(true); refresh(); }}
        />
      )}

      {!loading && data && (
        <>
          {/*
            What the SELECTED business unit's industry packs contribute.
            Tier 1 below is identical for every unit, so without this the page
            looked the same whether you picked a hospital or a bank - which is
            precisely why the packs appeared to do nothing.
          */}
          {data.industryPacks && (
            <GlassCard className="p-5 mb-6">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-sm font-semibold text-foreground/90">
                    Industry pack detectors
                    {selectedBusinessUnitName ? ` - ${selectedBusinessUnitName}` : ' - tenant default'}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Detectors these packs add on top of Tier 1. One switch per detector - it
                    governs every term listed on it. Switching off applies
                    {selectedBusinessUnitName ? ` to ${selectedBusinessUnitName}` : ' tenant-wide'} only;
                    other business units are unaffected.
                  </p>
                </div>
                <Badge variant={data.industryPacks.resolution === 'override' ? 'warning' : 'glass'}>
                  {data.industryPacks.resolution === 'override'
                    ? 'packs set on this unit'
                    : data.industryPacks.resolution === 'inherited'
                      ? 'inherited from parent unit'
                      : 'tenant default'}
                </Badge>
              </div>

              {data.industryPacks.detectors.length === 0 ? (
                <p className="text-xs text-muted-foreground mt-4">
                  These packs add no detectors. Tier 1 below still applies.
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-4">
                  {Object.entries(
                    // GROUP BY LABEL. The switch is keyed on the label, and one
                    // label carries many terms - health_record_id alone covers
                    // MRN, ABHA, EHR id and four more. Rendering a switch per
                    // TERM drew seven controls for one decision, so switching
                    // off "MRN" appeared to switch off ABHA too. It did not:
                    // there was only ever one switch, drawn seven times.
                    data.industryPacks.detectors.reduce<
                      Record<string, { pack: string; enabled: boolean; terms: typeof data.industryPacks.detectors }>
                    >((acc, d) => {
                      const g = acc[d.label] ?? { pack: d.pack, enabled: d.enabled !== false, terms: [] };
                      g.terms.push(d);
                      acc[d.label] = g;
                      return acc;
                    }, {}),
                  ).map(([label, g]) => {
                    const off = !g.enabled;
                    return (
                      <button
                        type="button"
                        key={label}
                        disabled={detectorBusy === label}
                        onClick={() => void toggleDetector(label, off)}
                        title={
                          off
                            ? `Switched off${selectedBusinessUnitName ? ` for ${selectedBusinessUnitName}` : ''} - click to switch on`
                            : `Switches off all ${g.terms.length} terms below for this business unit`
                        }
                        className={`text-left rounded-lg border p-3 transition-colors ${
                          off
                            ? 'border-border/30 opacity-45 hover:opacity-70'
                            : 'border-border/40 hover:border-primary/50'
                        }`}
                      >
                        <div className="flex items-center gap-2 flex-wrap">
                          <Power className={`h-3 w-3 ${off ? 'text-muted-foreground' : 'text-emerald-400'}`} />
                          <span className="font-mono text-sm">{label}</span>
                          <Badge variant="glass" className="text-[9px]">
                            {g.terms.length} term{g.terms.length === 1 ? '' : 's'}
                          </Badge>
                          {off && (
                            <Badge variant="glass" className="text-[9px]">switched off</Badge>
                          )}
                        </div>
                        {/* Every term the one switch governs, so the blast
                            radius is visible BEFORE clicking rather than
                            discovered after. */}
                        <div className="flex flex-wrap gap-1 mt-2">
                          {g.terms.map((t, i) => (
                            <span
                              key={`${label}-${t.pattern}-${i}`}
                              className="text-[10px] font-mono rounded bg-muted/40 px-1.5 py-0.5 text-muted-foreground"
                              title={t.match === 'value-pattern' ? 'matches values' : 'matches column names'}
                            >
                              {t.match === 'value-pattern' ? 'value: ' : ''}
                              {t.pattern.length > 28 ? `${t.pattern.slice(0, 28)}...` : t.pattern}
                            </span>
                          ))}
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-1">{g.pack}</div>
                      </button>
                    );
                  })}
                </div>
              )}

              {data.industryPacks.inertPackIds.length > 0 && (
                <p className="text-xs text-amber-300/90 mt-4">
                  {data.industryPacks.inertPackIds.length} attached pack
                  {data.industryPacks.inertPackIds.length === 1 ? '' : 's'} contribute nothing - no
                  content ships for: {data.industryPacks.inertPackIds.join(', ')}
                </p>
              )}
            </GlassCard>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            {[1, 2, 3, 4, 5].map((tier) => {
              const Icon = TIER_ICONS[tier as keyof typeof TIER_ICONS] ?? Cpu;
              const available = tierAvailability[tier];
              const td = TIER_DESCRIPTIONS[tier]!;
              const detectorsForTier = data.detectors.filter((d) => d.tier === tier);
              const notTypical = detectorsForTier.filter((d) => d.relevance === 'not_typical');
              return (
                <motion.div
                  key={tier}
                  className={tier === 1 ? 'lg:col-span-2' : ''}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: tier * 0.05 }}
                >
                  <GlassCard className={`p-5 h-full ${!available ? 'opacity-60' : ''}`}>
                    <CardHeader className="p-0 mb-3 flex-row items-start gap-3">
                      <div className="h-10 w-10 rounded-md bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center text-primary shrink-0">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-base">{td.name}</CardTitle>
                        <CardDescription className="text-xs">{td.subtitle}</CardDescription>
                      </div>
                      {available ? (
                        <Badge variant="success" className="text-[9px]">Active</Badge>
                      ) : (
                        <Badge variant="glass" className="text-[9px]">Roadmap</Badge>
                      )}
                    </CardHeader>
                    {detectorsForTier.length > 0 && (
                      <div className="mt-3">
                        <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
                          <span>{detectorsForTier.length} detectors - click to switch off</span>
                          <span>{detectorsForTier.filter((d) => d.enabled !== false).length} on</span>
                        </div>
                        <div className="grid gap-1.5 sm:grid-cols-2">
                        {tier === 1 && notTypical.length > 0 && (
                          <div className="col-span-full mb-2 flex items-start justify-between gap-3 rounded-md border border-border/40 bg-muted/20 px-3 py-2">
                            <p className="text-[11px] text-muted-foreground">
                              {notTypical.length} of these are rare in
                              {selectedBusinessUnitName ? ` ${selectedBusinessUnitName}` : ' this'}
                              &rsquo;s regulated records ({notTypical.map((d) => d.label).join(', ')}).
                              They stay ON - a hospital still takes card payments - but you can switch
                              them off for this business unit.
                            </p>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="shrink-0 text-[11px]"
                              onClick={() => {
                                for (const d of notTypical) {
                                  if (d.enabled !== false) void toggleDetector(d.label, false);
                                }
                              }}
                            >
                              Switch off {notTypical.length}
                            </Button>
                          </div>
                        )}
                        {detectorsForTier.map((d) => {
                          const on = d.enabled !== false;
                          const busy = detectorBusy === d.label;
                          return (
                            <button
                              key={d.name}
                              type="button"
                              disabled={busy}
                              onClick={() => void toggleDetector(d.label, !on)}
                              title={`Regulations: ${d.regulations.join(', ')}. Click to ${on ? 'disable' : 'enable'}.`}
                              className={`w-full flex items-start gap-2 text-left rounded-md px-2 py-1.5 border transition-colors ${on ? 'border-emerald-500/25 bg-emerald-500/5 hover:bg-emerald-500/10' : 'border-border/40 bg-muted/20 hover:bg-muted/30'}`}
                            >
                              {busy ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin mt-0.5 shrink-0 text-muted-foreground" />
                              ) : (
                                <Power className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${on ? 'text-emerald-400' : 'text-muted-foreground'}`} />
                              )}
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center flex-wrap gap-1.5">
                                  <span className={`font-mono text-xs font-medium ${on ? 'text-foreground' : 'text-muted-foreground line-through'}`}>
                                    {d.label}
                                  </span>
                                  {d.checksum && (
                                    <Badge variant="success" className="text-[8px] gap-0.5">
                                      <ShieldCheck className="h-2.5 w-2.5" />{d.checksum} checksum
                                    </Badge>
                                  )}
                                  {d.relevance === 'core' && (
                                    <Badge variant="success" className="text-[8px]">
                                      core for this industry
                                    </Badge>
                                  )}
                                  {d.relevance === 'not_typical' && (
                                    <Badge variant="glass" className="text-[8px]">
                                      rare in this industry
                                    </Badge>
                                  )}
                                  {d.indiaSpecific && (
                                    <Badge variant="glass" className="text-[8px]">India</Badge>
                                  )}
                                  {!on && <span className="text-[9px] text-muted-foreground">disabled</span>}
                                </div>
                                <div className="text-[11px] text-muted-foreground leading-snug">
                                  {DETECTOR_DESCRIPTIONS[d.label] ?? `Detects ${d.label.replace(/_/g, ' ')} values.`}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                        </div>
                      </div>
                    )}
                    {tier > 1 && !available && (
                      <div className="text-xs text-muted-foreground mt-3">
                        Phase plan: see <code>docs/poc-roadmap/00-MASTER-PLAN.md §3</code>
                      </div>
                    )}
                  </GlassCard>
                </motion.div>
              );
            })}
          </div>

          <GlassCard className="p-5 mb-6">
            <CardHeader className="p-0 mb-3 flex-row items-center justify-between">
              <div>
                <CardTitle>Your own rules</CardTitle>
                <CardDescription>
                  Add a custom detector and attach a checksum, so a match only confirms when the math passes -
                  the same false-positive killer our built-ins use, for your patterns. Our detector internals stay hidden.
                </CardDescription>
              </div>
              <Badge variant="glass" className="text-[10px]">
                {data.tenantOverlay.customDetectors.length} custom
              </Badge>
            </CardHeader>

            {/* Builder */}
            <div className="grid grid-cols-12 gap-2 items-end mb-3">
              <label className="col-span-3 text-[10px] uppercase text-muted-foreground">
                Label
                <input
                  value={ruleLabel}
                  onChange={(e) => setRuleLabel(e.target.value)}
                  className="mt-1 block w-full glass-lite rounded px-2 py-1.5 text-xs font-mono bg-transparent border border-border/40 focus:outline-none focus:border-primary"
                />
              </label>
              <label className="col-span-4 text-[10px] uppercase text-muted-foreground">
                Regex pattern
                <input
                  value={rulePattern}
                  onChange={(e) => setRulePattern(e.target.value)}
                  spellCheck={false}
                  className="mt-1 block w-full glass-lite rounded px-2 py-1.5 text-xs font-mono bg-transparent border border-border/40 focus:outline-none focus:border-primary"
                />
              </label>
              <label className="col-span-3 text-[10px] uppercase text-muted-foreground">
                Checksum
                <select
                  value={ruleChecksum}
                  onChange={(e) => setRuleChecksum(e.target.value as ClassifierChecksum)}
                  className="mt-1 block w-full glass-lite rounded px-2 py-1.5 text-xs bg-transparent border border-border/40 focus:outline-none focus:border-primary"
                >
                  {CHECKSUM_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </label>
              <div className="col-span-2">
                <Button
                  className="w-full"
                  size="sm"
                  onClick={() => void addCustomRule()}
                  disabled={ruleBusy || !ruleLabel.trim() || !rulePattern.trim()}
                >
                  {ruleBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Plus className="h-3 w-3 mr-1" />}
                  Add rule
                </Button>
              </div>
            </div>
            {ruleMsg && (
              <div className="text-[11px] mb-3 glass-lite rounded-md p-2 border border-emerald-500/30 text-emerald-200">
                {ruleMsg}
              </div>
            )}

            {/* Existing custom rules */}
            {data.tenantOverlay.customDetectors.length === 0 ? (
              <div className="text-xs text-muted-foreground py-2 text-center">
                No custom rules yet. Add one above, then test it in the panel below. Advanced pack-scoped rules live in{' '}
                <Link to="/admin/classifier-vocab" className="text-primary hover:underline">Classifier Vocab</Link>.
              </div>
            ) : (
              <div className="space-y-1.5">
                {data.tenantOverlay.customDetectors.map((d) => (
                  <div key={d.id} className="flex items-center gap-2 text-xs glass-lite rounded-md px-2.5 py-1.5">
                    <span className={`h-2 w-2 rounded-full ${d.enabled ? 'bg-emerald-400' : 'bg-muted-foreground'}`} />
                    <span className="font-medium">{d.label}</span>
                    <code className="text-[10px] text-muted-foreground font-mono truncate max-w-[220px]">/{d.pattern}/</code>
                    {d.checksum && d.checksum !== 'none' && (
                      <Badge variant="success" className="text-[8px] ml-auto">
                        <ShieldCheck className="h-2.5 w-2.5 mr-0.5" />{d.checksum}
                      </Badge>
                    )}
                    <Badge variant="glass" className="text-[8px]">{d.patternKind}</Badge>
                  </div>
                ))}
              </div>
            )}
          </GlassCard>

          <GlassCard className="p-5 flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 text-success shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="font-medium">Voter aggregation</div>
              <p className="text-xs text-muted-foreground mt-0.5">
                When multiple tiers fire, the voter weights each tier&rsquo;s
                confidence and produces a final label + confidence per column.
                Source: <code>packages/classifiers/src/voter.ts</code>.
              </p>
            </div>
          </GlassCard>
        </>
      )}

      {/* Wave 5 Group-A (2026-05-27) - on-demand classify panel */}
      <GlassCard className="p-5 mt-4">
        <CardHeader className="p-0 mb-3">
          <CardTitle className="flex items-center gap-2 text-sm">
            <FlaskConical className="h-4 w-4 text-primary" /> Classify samples (on-demand)
          </CardTitle>
          <CardDescription>
            Paste a column name + a few sample values to see what the multi-engine voter
            decides. Wraps <code className="font-mono text-[10px]">POST /v1/discovery/classify</code>.
            <span className="block mt-1 text-[10px] text-muted-foreground">
              Diagnostic only - nothing here changes your scanners. Detector toggles and custom
              rules above DO persist; this panel does not. Deployed agents detect OCR text
              automatically, and OCR is enabled by shipping the Tika sidecar alongside the agent.
            </span>
          </CardDescription>
        </CardHeader>
        <div className="grid grid-cols-12 gap-3 text-xs items-end">
          <div className="col-span-4">
            <div className="text-[10px] uppercase text-muted-foreground mb-1">Column name</div>
            <input
              type="text"
              value={classifyCol}
              onChange={(e) => setClassifyCol(e.target.value)}
              className="glass-lite w-full px-3 py-1.5 rounded-md text-xs border border-border/40 focus:outline-none focus:border-primary"
            />
          </div>
          <div className="col-span-6">
            <div className="text-[10px] uppercase text-muted-foreground mb-1">
              Sample values (one per line)
            </div>
            <textarea
              value={classifySamples_}
              onChange={(e) => setClassifySamples_(e.target.value)}
              rows={4}
              spellCheck={false}
              className="glass-lite w-full px-3 py-2 rounded-md text-[11px] font-mono border border-border/40 focus:outline-none focus:border-primary"
            />
          </div>
          <div className="col-span-2 space-y-2">
            {/* OCR provenance switch. Scanned documents lose characters
                (Tesseract read a real Aadhaar's 6 as an 8), which fails the
                checksum and silently drops a real identifier. With this on, the
                voter attempts a single-character OCR repair and marks anything
                it cannot confirm as `ocr_unverified` rather than discarding it. */}
            <label
              className="flex items-start gap-2 cursor-pointer select-none"
              title="TEST CONTROL - affects this on-demand check only. It does not configure your scanners. Real scans detect OCR text automatically (the agent knows it routed the file to OCR). OCR itself is switched on by deploying the Tika sidecar with the agent."
            >
              <input
                type="checkbox"
                checked={classifyFromOcr}
                onChange={(e) => setClassifyFromOcr(e.target.checked)}
                className="mt-0.5"
              />
              <span className="text-[10px] leading-tight">
                <span className="font-medium">Simulate OCR text</span>
                <span className="block text-muted-foreground">this test only</span>
              </span>
            </label>
            <Button
              className="w-full"
              size="sm"
              onClick={() => void handleClassify()}
              disabled={classifying || !classifyCol.trim()}
            >
              {classifying ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <FlaskConical className="h-3 w-3 mr-1" />}
              {classifying ? 'Classifying…' : 'Classify'}
            </Button>
          </div>
          {classifyError && (
            <div className="col-span-12 glass-lite rounded-md p-2 text-[11px] text-destructive border border-destructive/40">
              {classifyError}
            </div>
          )}
          {classifyResult && (
            <div className="col-span-12 glass-lite rounded-md p-3 space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="success">{classifyResult.label}</Badge>
                <span className="text-[10px] text-muted-foreground tabular">
                  {(classifyResult.confidence * 100).toFixed(1)}% conf · tier {classifyResult.tier}
                </span>
                {classifyResult.ocrUnverified && (
                  <Badge
                    variant="warning"
                    className="text-[9px]"
                    title="This value was RECONSTRUCTED from OCR damage, or matched the format without a confirmable checksum. Verify against the source document before acting on it."
                  >
                    ocr_unverified
                  </Badge>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground leading-snug">
                {classifyResult.rationale}
              </p>
              {classifyResult.validation && (() => {
                const v = classifyResult.validation!;
                const allValid = v.validated === v.matched;
                const plural = v.matched === 1 ? '' : 's';
                return (
                  <div
                    className={`text-[11px] rounded-md px-2 py-1.5 border leading-snug ${
                      allValid
                        ? 'border-emerald-500/40 text-emerald-300'
                        : 'border-amber-500/40 text-amber-300'
                    }`}
                  >
                    <span className="font-semibold tabular">
                      {v.validated} of {v.matched}
                    </span>{' '}
                    value{plural} matching the {classifyResult.label} format passed the{' '}
                    {v.checksum} checksum
                    {!allValid && (
                      <span className="text-muted-foreground">
                        {' '}
                        - the other {v.matched - v.validated} matched the format but failed the
                        checksum (fake or mistyped, not a real {classifyResult.label}).
                      </span>
                    )}
                  </div>
                );
              })()}
              {classifyResult.alternateLabels.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  <span className="text-[10px] uppercase text-muted-foreground">Alternates:</span>
                  {classifyResult.alternateLabels.map((a) => (
                    <code key={a.label} className="text-[10px] glass-lite px-1.5 py-0.5 rounded">
                      {a.label} ({(a.confidence * 100).toFixed(0)}%)
                    </code>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </GlassCard>
    </PageContainer>
  );
}
